import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, matchPath, RouteProps } from 'react-router-dom';
import { useRouteHelper } from './routesHelpers';
import { flatten } from 'flat';

interface MatchingRouteMeta {
  key: string;
  params: Record<string, string>;
}

const useChangeLanguage = (): {
  currentLang: string;
  languages: string[];
  changeLanguage: (newLang: string) => void;
} => {
  const { i18n } = useTranslation('routes');
  const { setRoute } = useRouteHelper();
  const history = useHistory();
  const location = useLocation();
  const defaultMatchingRoute: MatchingRouteMeta = {
    key: 'home',
    params: {},
  };
  const currentLang = i18n.language;
  const languages = (i18n?.options?.supportedLngs || []).filter((lang) => lang !== 'cimode');

  function getCurrentRouteMeta(): MatchingRouteMeta {
    const { routes } = i18n.services.backendConnector.store.data[currentLang];
    // @ts-ignore
    const routeKeys = Object.entries(flatten(routes));
    const pathToMatch = location.pathname.split(`/${currentLang}`)[1];

    return routeKeys.reduce((result, [key, path]) => {
      const match = matchPath(pathToMatch, {
        path,
        exact: true,
        strict: false,
      } as RouteProps);
      const routeMatch = match
        ? {
            key,
            params: match.params,
          }
        : null;
      return { ...result, ...routeMatch } || defaultMatchingRoute;
    }, defaultMatchingRoute);
  }

  async function changeLanguage(newLang: string) {
    await i18n.changeLanguage(newLang);
    const routeMeta = getCurrentRouteMeta();
    history.push(setRoute(routeMeta.key, routeMeta.params, newLang));
  }

  return {
    currentLang,
    languages,
    changeLanguage,
  };
};

export default useChangeLanguage;
